import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Updater } from '@tanstack/react-table';
import { AssetClassToLoanType } from 'app-level/config/assetClassTypes';
import {
  BaseTableProps,
  LoanColumnDef,
  LoanTable,
  getDefaultColumns,
} from 'features/drilldown/LoanDatatable/LoanTable';
import { TableQueryParameters } from 'hooks/useTableQueryParams';
import keyBy from 'lodash/keyBy';
import isEqual from 'fast-deep-equal';
import { useDialog } from 'ui-kit';

import { DealRole } from '__generated__/globalTypes';
import { useLoanDiligence } from './contexts/LoanDiligenceProvider';
import DiligenceNotesDialog from './DiligenceNotesDialog';
import { DiligenceNotesIconButton } from './DiligenceNotesIconButton';
import { LoanDiligenceStatus, LoanStatusDropdown } from './LoanDiligenceStatus';
import { DataContext, DataContextActionType } from '../../../../context/DataContext';

import { GetDealDiligence } from 'query/__generated__/diligence';
import { GetDealDiligence_deal_diligence } from 'query/diligence';
type GetDealDiligence_deal = NonNullable<GetDealDiligence>['deal'];
export type LoanDiligenceStatusMap = {
  [loanId: string]: LoanDiligenceStatus;
};

function diligenceStatusSignature(
  diligence: GetDealDiligence_deal_diligence[] | undefined,
): string {
  if (!diligence?.length) return '';
  return [...diligence]
    .sort((a, b) => a.loan.id.localeCompare(b.loan.id))
    .map((d) => `${d.loan.id}:${d.status}`)
    .join('|');
}

interface DiligenceLoanTableProps<K extends keyof AssetClassToLoanType> {
  deal?: GetDealDiligence_deal | null;
  role: DealRole;
  assetClass: K;

  sorting: BaseTableProps<K>['sorting'];

  onViewClick: (loanId: string) => void;
  onQuickStatusClick: (loanId: string) => void;
  onLoanDiligenceStatusChanged: (loanId: string, status: LoanDiligenceStatus) => void;
  pagination: {
    updateParams: (newParams: Partial<TableQueryParameters<any, any>>) => void;

    queryParams: TableQueryParameters<any, any>;
    totalItems?: number;
  };
}

const _ReviewDiligenceTable = <K extends keyof AssetClassToLoanType>({
  deal,
  role,
  assetClass,
  sorting,
  onViewClick,
  onQuickStatusClick,
  onLoanDiligenceStatusChanged,
  pagination,
}: DiligenceLoanTableProps<K>) => {
  /* eslint-disable */
  const loanDiligence = useLoanDiligence();
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);
  const diligenceNoteDialog = useDialog();
  const { state, dispatch } = useContext(DataContext);

  const computedColumnOrder = useMemo(() => {
    const savedOrder = state.globalTableOrderConfig.myPortfolio.loanTable[assetClass] ?? [];
    const orderWithoutStatus = (savedOrder as string[]).filter((colId) => colId !== 'status');
    return ['status', ...orderWithoutStatus];
  }, [state.globalTableOrderConfig.myPortfolio.loanTable[assetClass], assetClass]);

  const setColumnOrder = useCallback(
    (updater: Updater<string[]>) => {
      const newOrder = typeof updater === 'function' ? updater(computedColumnOrder) : updater;

      const filteredOrder = newOrder.filter((colId) => colId !== 'status');

      dispatch({
        type: DataContextActionType.SET_GLOBAL_TABLE_ORDER_CONFIG,
        payload: {
          ...state.globalTableOrderConfig,
          myPortfolio: {
            ...state.globalTableOrderConfig.myPortfolio,
            loanTable: {
              ...state.globalTableOrderConfig.myPortfolio.loanTable,
              [assetClass]: filteredOrder as (keyof AssetClassToLoanType[K])[],
            },
          },
        },
      });
    },
    [computedColumnOrder, state.globalTableOrderConfig, assetClass, dispatch],
  );

  const stateWithStatusFirst = useMemo(
    () => ({
      ...state,
      globalTableOrderConfig: {
        ...state.globalTableOrderConfig,
        myPortfolio: {
          ...state.globalTableOrderConfig.myPortfolio,
          loanTable: {
            ...state.globalTableOrderConfig.myPortfolio.loanTable,
            [assetClass]: computedColumnOrder as (keyof AssetClassToLoanType[K])[],
          },
        },
      },
    }),
    [state, computedColumnOrder, assetClass],
  );

  const [localStatusChanges, setLocalStatusChanges] = useState<{
    [loanId: string]: LoanDiligenceStatus;
  }>({});

  const diligenceLoans = useMemo(() => {
    const loans =
      deal?.diligence?.map((diligence) => {
        const loan = diligence.loan as AssetClassToLoanType[K];
        return {
          ...loan,
          status: localStatusChanges[loan.id] || diligence.status,
        };
      }) || [];
    return loans;
  }, [deal?.diligence, localStatusChanges]);

  const loanIdToDiligenceMap = useMemo(() => {
    if (!deal) return {};
    const baseMap = keyBy(deal.diligence, (diligence) => diligence.loan.id);
    return Object.keys(baseMap).reduce(
      (acc, loanId) => {
        acc[loanId] = {
          ...baseMap[loanId],
          status: localStatusChanges[loanId] || baseMap[loanId].status,
        };
        return acc;
      },
      {} as { [loanId: string]: GetDealDiligence_deal_diligence },
    );
  }, [deal, localStatusChanges]);

  const selectedDiligence = useMemo(
    () => (selectedLoanId ? loanIdToDiligenceMap[selectedLoanId] : null),
    [loanIdToDiligenceMap, selectedLoanId],
  );

  const handleNoteIconClick = useCallback(
    (loanId: string) => {
      setSelectedLoanId(loanId);
      diligenceNoteDialog.setIsOpen(true);
    },
    [diligenceNoteDialog],
  );

  const diligenceStatusSig = useMemo(
    () => diligenceStatusSignature(deal?.diligence),
    [deal?.diligence],
  );

  useEffect(() => {
    setLocalStatusChanges({});
  }, [deal?.id, diligenceStatusSig]);

  const handleViewClick = useCallback(
    (id: string) => {
      onViewClick?.(id);
    },
    [onViewClick],
  );

  const handleQuickStatusClick = useCallback(
    (id: string) => {
      onQuickStatusClick?.(id);
    },
    [onQuickStatusClick],
  );

  const handleLoanDiligenceStatusChanged = useCallback(
    (id: string, status: LoanDiligenceStatus) => {
      setLocalStatusChanges((prev) => ({ ...prev, [id]: status }));
      onLoanDiligenceStatusChanged(id, status);
    },
    [onLoanDiligenceStatusChanged],
  );

  const statusColumn: LoanColumnDef<K> = useMemo(
    () => ({
      header: 'Status',
      id: 'status',
      cell: (props) => {
        const hoverLeftOffset = (props as any).hoverLeftOffset as number;

        const diligence = loanIdToDiligenceMap[props.row.original.id];
        if (!diligence) {
          return <div>No diligence data</div>;
        }

        const status = diligence.status;

        return (
          <div className="flex items-center">
            <div className="hidden group-hover:block">
              <div
                className={`absolute bottom-0 right-0 z-10 flex  items-center justify-end gap-6`}
                style={{ transform: `translateX(-${hoverLeftOffset}px)` }}
              >
                <button
                  className="text-[#d743bd] underline hover:text-accent2-emphasis"
                  type="button"
                  onClick={() => handleViewClick(props.row.original.id)}
                >
                  View
                </button>
                <button
                  className="text-[#d743bd] underline hover:text-accent2-emphasis"
                  type="button"
                  onClick={() => handleQuickStatusClick(props.row.original.id)}
                >
                  Quick Status View
                </button>
                <DiligenceNotesIconButton
                  diligenceId={diligence.id}
                  onClick={() => handleNoteIconClick(props.row.original.id)}
                />
              </div>
            </div>

            {role === DealRole.SELLER ? (
              <div>{status}</div>
            ) : (
              <LoanStatusDropdown
                value={status}
                onChange={(newStatus) =>
                  handleLoanDiligenceStatusChanged(props.row.original.id, newStatus)
                }
              />
            )}
          </div>
        );
      },
    }),
    [
      loanIdToDiligenceMap,
      role,
      handleLoanDiligenceStatusChanged,
      handleViewClick,
      handleQuickStatusClick,
      handleNoteIconClick,
    ],
  );

  const columns = useMemo(() => {
    const defaultColumns = getDefaultColumns(
      assetClass,
      false,
      state.globalTableOrderConfig.myPortfolio.loanTable[assetClass] ?? [],
    );

    // Always put status column first, remove it from default columns if present
    const filteredColumns = defaultColumns.filter((col) => col.id !== 'status');

    const finalColumns = [statusColumn, ...filteredColumns];

    return finalColumns;
  }, [
    statusColumn,
    assetClass,
    state.globalTableOrderConfig.myPortfolio.loanTable[assetClass],
    computedColumnOrder,
  ]);
  /* eslint-enable */ //TODO: fixed React Hook "useDialog" is called in function

  if (!deal) {
    return null;
  }

  return (
    <div className="relative">
      {loanDiligence.processingLoanIds.length > 0 && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50">
          <div className="text-xl text-white">
            Processing diligence for {loanDiligence.processingLoanIds.length} loans... please wait.
          </div>
        </div>
      )}
      <DataContext.Provider value={{ state: stateWithStatusFirst, dispatch }}>
        <LoanTable
          data={diligenceLoans}
          strings={{
            empty:
              role === DealRole.SELLER
                ? 'Waiting for buyer to select loans'
                : 'Please select loans',
          }}
          sorting={sorting}
          assetClass={assetClass}
          // @ts-expect-error - LoanColumnDef type mismatch with generic constraints, needs refactoring
          columns={columns}
          pagination={pagination}
          omitColumnIds={role === DealRole.BUYER ? ['listing'] : []}
          setColumnOrder={setColumnOrder}
        />
      </DataContext.Provider>
      {selectedDiligence && (
        <DiligenceNotesDialog
          dialog={diligenceNoteDialog}
          documentName={selectedDiligence?.loan.account_id || 'Unknown Loan'}
          diligenceId={selectedDiligence?.id}
        />
      )}
    </div>
  );
};

export const ReviewDiligenceTable = memo(_ReviewDiligenceTable, (prevProps, nextProps) => {
  return (
    prevProps.deal === nextProps.deal &&
    prevProps.role === nextProps.role &&
    prevProps.assetClass === nextProps.assetClass &&
    isEqual(prevProps.sorting, nextProps.sorting) &&
    isEqual(prevProps.pagination, nextProps.pagination)
  );
});
